import { hasProp, hasPropOfType, isObject, isString } from "src/utils";
import { HttpError, RequestAbortedError, RequestError } from "./errors";
import { HttpStatus, type QueryFnError } from "./types";

/**
 * Checks if value is instance of Error. Useful to narrow down unknown from catch.
 *
 * @param value
 */
export const isError = (value: unknown): value is Error => value instanceof Error;

/**
 * Checks if value is instance of {@link HttpError}.
 *
 * @param value
 * @see HttpError
 */
export const isHttpError = (value: unknown): value is HttpError => value instanceof HttpError;

/**
 * Checks if value is instance of {@link RequestError}.
 *
 * @param value
 * @see RequestError
 */
export const isRequestError = (value: unknown): value is RequestError => value instanceof RequestError;

/**
 * Checks if value is instance of {@link RequestAbortedError}.
 *
 * @param value
 * @see RequestAbortedError
 */
export const isRequestAbortedError = (value: unknown): value is RequestAbortedError => value instanceof RequestAbortedError;

/**
 * Checks if value is instance of {@link HttpError} with status {@link HttpStatus.unauthorized}.
 *
 * @param value
 * @see HttpError
 */
export const isUnauthorizedError = (value: unknown): value is HttpError => isHttpError(value) && value.status === HttpStatus.unauthorized;

/**
 * Checks if value is instance of {@link HttpError} with status {@link HttpStatus.forbidden}.
 *
 * @param value
 * @see HttpError
 */
export const isForbiddenError = (value: unknown): value is HttpError => isHttpError(value) && value.status === HttpStatus.forbidden;

/**
 * Checks if value is instance of {@link HttpError} with status {@link HttpStatus.notFound}.
 *
 * @param value
 * @see HttpError
 */
export const isNotFoundError = (value: unknown): value is HttpError => isHttpError(value) && value.status === HttpStatus.notFound;

/**
 * Creates {@link QueryFnError} object.
 * @param message Error message.
 * @param originalError Original error object. If it is instance of Error, its message is used. Otherwise, it is converted to string.
 * @param queryArgs Structured query arguments. Should be serializable to JSON if remote logging is used.
 */
export const createQueryFnError = (message: string, originalError: unknown, queryArgs: unknown): QueryFnError => ({
    message,
    originalError: originalError instanceof Error ? originalError.message : String(originalError),
    queryArgs,
});

export const isQueryFnError = (value: unknown): value is QueryFnError =>
    isObject(value) && hasPropOfType(value, "message", isString) && hasProp(value, "originalError") && hasProp(value, "queryArgs");
